(() => {
  const addonSelector = '[data-sz-service-addon]';
  const steelCardSelector = '[data-sz-steel-choice]';

  const getRoot = (node) => node?.closest?.('product-info');

  const parseJson = (node) => {
    if (!node) return [];
    try {
      return JSON.parse(node.textContent || '[]');
    } catch (error) {
      console.error('SportsZone configurator JSON error', error);
      return [];
    }
  };

  const variantNumericId = (value) => {
    const match = String(value || '').match(/(\d+)$/);
    return match ? Number(match[1]) : 0;
  };

  const money = (cents) => {
    const amount = Number(cents || 0) / 100;
    const currency = window.Shopify?.currency?.active || 'CAD';
    const locale = document.documentElement.lang || 'en-CA';

    try {
      return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const padHolder = (holder) => String(holder || '').padStart(2, '0');

  const holderLength = {
    4: 238,
    5: 246,
    6: 254,
    7: 263,
    8: 272,
    9: 280,
    10: 288,
    11: 296,
    12: 306,
  };

  const holderForSkate = (sizeValue, fitValue) => {
    const size = Number.parseFloat(String(sizeValue || '').replace(/[^\d.]/g, ''));
    if (!Number.isFinite(size)) return null;

    const fit = String(fitValue || '').replace(/\s+/g, '').toUpperCase();
    const isFit3 = fit === 'FIT3' || fit === '3';

    if (isFit3) {
      if (size >= 4 && size <= 4.5) return 4;
      if (size >= 5 && size <= 12) return Math.floor(size + 0.5);
      return null;
    }

    if (size >= 4 && size <= 12) return Math.floor(size);
    return null;
  };

  const getProductForm = (root) => root?.querySelector('product-form');
  const getForm = (root) => getProductForm(root)?.querySelector('form');

  const getSelectedVariant = (root, card) => {
    const form = getForm(root);
    const id = variantNumericId(form?.querySelector('input[name="id"]')?.value);
    const variants = parseJson(card?.querySelector('[data-sz-skate-variants]'));
    return variants.find((variant) => variantNumericId(variant.id) === id) || null;
  };

  const getOptionName = (group) => {
    const label = group.querySelector('legend, .form__label');
    if (!label) return '';
    const text = label.textContent.replace(/\s+/g, ' ').trim();
    return (text.includes(':') ? text.split(':')[0] : text).trim().toLowerCase();
  };

  const getSelectedOption = (root, wanted) => {
    const groups = Array.from(
      root?.querySelectorAll('variant-radios .product-form__input, variant-selects .product-form__input') || []
    );

    for (const group of groups) {
      const name = getOptionName(group);
      if (!wanted.some((keyword) => name.includes(keyword))) continue;

      const selected = group.querySelector('input[type="radio"]:checked, select option:checked');
      const select = group.querySelector('select');

      if (selected?.value) return String(selected.value).trim();
      if (select?.value) return String(select.value).trim();
    }

    return '';
  };

  const findRunnerVariant = (card, steelName, holder) => {
    if (!card || !holder) return null;
    const selector = steelName === 'FLY-TI' ? '[data-sz-fly-ti-variants]' : '[data-sz-fly-x-variants]';
    const variants = parseJson(card.querySelector(selector));

    return (
      variants.find((variant) => {
        const titleNumber = Number.parseInt(String(variant.title || '').replace(/[^\d]/g, ''), 10);
        return titleNumber === holder;
      }) || null
    );
  };

  const clearSteelError = (root) => {
    const error = root?.querySelector('[data-sz-steel-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const showSteelError = (root, message, focusTarget) => {
    const error = root?.querySelector('[data-sz-steel-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    focusTarget?.focus?.();
  };

  const removeSteelProperties = (form) => {
    form?.querySelectorAll('[data-sz-steel-property]').forEach((input) => input.remove());
  };

  const addSteelProperty = (form, name, value) => {
    if (!form || !value) return;
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${name}]`;
    input.value = value;
    input.dataset.szSteelProperty = '';
    form.append(input);
  };

  const buildSteelProperties = (state) => {
    if (!state?.steelName || !state?.holder) return {};

    return {
      'Steel choice': `BAUER ${state.steelName}`,
      'Runner size': `${padHolder(state.holder)} / ${holderLength[state.holder]} mm`,
      'Steel package': state.isSale
        ? 'Boot-only sale — runner added separately'
        : 'Included with regular skate package',
    };
  };

  const injectSteelProperties = (root, state) => {
    const form = getForm(root);
    if (!form) return;

    removeSteelProperties(form);
    const properties = buildSteelProperties(state);
    Object.entries(properties).forEach(([name, value]) => addSteelProperty(form, name, value));
  };

  const setSteelOptionAvailability = (card, steelName, runnerVariant) => {
    const option = card.querySelector(`[data-sz-steel-option="${steelName}"]`);
    const input = option?.querySelector('[data-sz-steel-input]');
    const stock = card.querySelector(`[data-sz-steel-stock="${steelName}"]`);

    const available = Boolean(runnerVariant && runnerVariant.available !== false);
    option?.classList.toggle('is-unavailable', !available);

    if (input) {
      input.disabled = !available;
      if (!available && input.checked) input.checked = false;
    }

    if (stock) {
      stock.textContent = runnerVariant
        ? available
          ? `Runner ${padHolder(Number.parseInt(runnerVariant.title, 10))} available`
          : `Runner ${padHolder(Number.parseInt(runnerVariant.title, 10))} sold out`
        : 'No matching runner';
    }

    return available;
  };

  const syncSteelCard = (root) => {
    const card = root?.querySelector(steelCardSelector);
    if (!card) return null;

    clearSteelError(root);

    const variant = getSelectedVariant(root, card);
    const size = getSelectedOption(root, ['size']);
    const fit = getSelectedOption(root, ['fit', 'width']);
    const holder = holderForSkate(size, fit);
    const mm = holder ? holderLength[holder] : null;
    const isSale = Boolean(
      variant &&
      Number(variant.compare_at_price || 0) > 0 &&
      Number(variant.compare_at_price) > Number(variant.price || 0)
    );

    const priorSale = card.dataset.szSaleState === 'true';
    card.dataset.szSaleState = isSale ? 'true' : 'false';
    card.classList.toggle('is-boot-only-sale', isSale);
    root.classList.toggle('sz-steel-sale', isSale);

    const badge = card.querySelector('[data-sz-steel-sale-badge]');
    const saleNote = card.querySelector('[data-sz-steel-sale-note]');
    const intro = card.querySelector('[data-sz-steel-intro]');
    if (badge) badge.hidden = !isSale;
    if (saleNote) saleNote.hidden = !isSale;

    if (intro) {
      intro.textContent = isSale
        ? 'This sale price is for the boots only. Choose a runner below and the correct pair will be added to your cart separately.'
        : "This skate's regular package price is based on FLY-TI runners. Choose which steel you want us to install.";
    }

    const fitLine = card.querySelector('[data-sz-runner-size]');
    if (fitLine) {
      fitLine.textContent = holder && mm
        ? `Skate ${size}${fit ? ` / ${fit}` : ''} → runner ${padHolder(holder)} (${mm} mm)`
        : 'Choose a skate size and fit';
    }

    const tiVariant = findRunnerVariant(card, 'FLY-TI', holder);
    const xVariant = findRunnerVariant(card, 'FLY-X', holder);
    const tiAvailable = setSteelOptionAvailability(card, 'FLY-TI', tiVariant);
    const xAvailable = setSteelOptionAvailability(card, 'FLY-X', xVariant);

    const tiPrice = card.querySelector('[data-sz-steel-price="FLY-TI"]');
    const xPrice = card.querySelector('[data-sz-steel-price="FLY-X"]');
    if (tiPrice) tiPrice.textContent = isSale && tiVariant ? `+${money(tiVariant.price)}` : 'Included';
    if (xPrice) xPrice.textContent = isSale && xVariant ? `+${money(xVariant.price)}` : 'Alternative';

    const selected = card.querySelector('[data-sz-steel-input]:checked');

    if (isSale && !priorSale && selected) {
      selected.checked = false;
    }

    if (!isSale && !card.querySelector('[data-sz-steel-input]:checked')) {
      const preferred = tiAvailable
        ? card.querySelector('[data-sz-steel-input][value="FLY-TI"]')
        : xAvailable
          ? card.querySelector('[data-sz-steel-input][value="FLY-X"]')
          : null;
      if (preferred) preferred.checked = true;
    }

    const steelName = card.querySelector('[data-sz-steel-input]:checked')?.value || '';
    const runnerVariant = steelName === 'FLY-TI' ? tiVariant : steelName === 'FLY-X' ? xVariant : null;

    const state = { card, variant, size, fit, holder, mm, isSale, steelName, runnerVariant };
    injectSteelProperties(root, state);
    return state;
  };

  const validateSteel = (root) => {
    const card = root?.querySelector(steelCardSelector);
    if (!card) return null;

    const state = syncSteelCard(root);
    if (!state) return null;

    if (!state.holder || !state.mm) {
      showSteelError(root, 'Choose your skate size and fit so we can match the correct runner size.', root.querySelector('variant-radios input, variant-selects select'));
      return false;
    }

    if (!state.steelName) {
      showSteelError(
        root,
        state.isSale
          ? 'This is a boot-only sale. Choose FLY-TI or FLY-X runners before adding the skates to cart.'
          : 'Choose FLY-TI or FLY-X runners before adding the skates to cart.',
        card.querySelector('[data-sz-steel-input]:not([disabled])')
      );
      return false;
    }

    if (!state.runnerVariant || state.runnerVariant.available === false) {
      showSteelError(root, `The ${state.steelName} runner for this skate size is not available. Choose the other steel option or another skate size.`, card.querySelector('[data-sz-steel-input]:not([disabled])'));
      return false;
    }

    injectSteelProperties(root, state);
    return state;
  };

  const clearConfigError = (root) => {
    const error = root?.querySelector('[data-sz-addon-error]');
    if (!error) return;
    error.textContent = '';
    error.hidden = true;
  };

  const showConfigError = (root, message, focusTarget) => {
    const error = root?.querySelector('[data-sz-addon-error]');
    if (error) {
      error.textContent = message;
      error.hidden = false;
    }
    focusTarget?.focus?.();
  };

  const syncOtherSharpening = (root) => {
    const selected = root?.querySelector('[data-sz-sharpen]:checked');
    const wrap = root?.querySelector('[data-sz-sharpen-other-wrap]');
    const input = root?.querySelector('[data-sz-sharpen-other]');
    if (!wrap) return;

    const isOther = selected?.value === 'Other';
    wrap.hidden = !isOther;
    if (!isOther && input) input.value = '';
  };

  const syncAddonState = (input) => {
    const root = getRoot(input);
    if (!root) return;

    const config = root.querySelector('[data-sz-addon-config]');
    root.classList.toggle('sz-addon-selected', input.checked);
    if (config) config.hidden = !input.checked;

    if (!input.checked) clearConfigError(root);
    syncOtherSharpening(root);
  };

  const initConfigurators = (scope = document) => {
    scope.querySelectorAll?.(addonSelector).forEach(syncAddonState);
    scope.querySelectorAll?.('product-info').forEach((root) => syncSteelCard(root));
  };

  const getServiceConfig = (root) => {
    const card = root?.querySelector('[data-sz-service-addon-card]');
    if (!card || card.dataset.szAddonKind !== 'skate') return {};

    clearConfigError(root);

    let profile = '';
    if (card.dataset.szRequiresProfile === 'true' || card.dataset.requiresProfile === 'true') {
      const profileInput = root.querySelector('[data-sz-profile]:checked');
      if (!profileInput) {
        showConfigError(root, 'Choose Zuperior, Quad, or Ellipse before adding the contouring service.', root.querySelector('[data-sz-profile]'));
        return null;
      }
      profile = profileInput.value;
    }

    const sharpenInput = root.querySelector('[data-sz-sharpen]:checked');
    if (!sharpenInput) {
      showConfigError(root, 'Choose a sharpening hollow before adding the contouring service.', root.querySelector('[data-sz-sharpen]'));
      return null;
    }

    let sharpening = sharpenInput.value;
    if (sharpening === 'Other') {
      const otherInput = root.querySelector('[data-sz-sharpen-other]');
      const otherValue = otherInput?.value?.trim();
      if (!otherValue) {
        showConfigError(root, 'Enter your preferred sharpening hollow.', otherInput);
        return null;
      }
      sharpening = `Other: ${otherValue}`;
    }

    return { profile, sharpening };
  };

  const scheduleSteelSync = (root) => {
    if (!root?.querySelector(steelCardSelector)) return;
    window.requestAnimationFrame(() => syncSteelCard(root));
    window.setTimeout(() => syncSteelCard(root), 90);
  };

  document.addEventListener('change', (event) => {
    const addon = event.target.closest?.(addonSelector);
    if (addon) {
      syncAddonState(addon);
      return;
    }

    const root = getRoot(event.target);
    if (!root) return;

    if (event.target.matches?.('[data-sz-steel-input]')) {
      clearSteelError(root);
      syncSteelCard(root);
      return;
    }

    if (event.target.matches?.('[data-sz-profile], [data-sz-sharpen]')) {
      clearConfigError(root);
      syncOtherSharpening(root);
      return;
    }

    if (
      event.target.matches?.(
        'variant-radios input[type="radio"], variant-selects select, variant-selects input[type="radio"]'
      )
    ) {
      scheduleSteelSync(root);
    }
  });

  document.addEventListener('input', (event) => {
    if (!event.target.matches?.('[data-sz-sharpen-other]')) return;
    clearConfigError(getRoot(event.target));
  });

  document.addEventListener('shopify:section:load', (event) => initConfigurators(event.target));
  initConfigurators();

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;

      const productForm = form.closest('product-form');
      if (!productForm) return;

      const root = productForm.closest('product-info');
      if (!root) return;

      const hasSteel = Boolean(root.querySelector(steelCardSelector));
      const addon = root.querySelector(`${addonSelector}:checked`);
      const steelState = hasSteel ? validateSteel(root) : null;

      if (hasSteel && steelState === false) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      const customAddRequired = Boolean(addon || steelState?.isSale);
      if (!customAddRequired) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const serviceConfig = addon ? getServiceConfig(root) : {};
      if (serviceConfig === null) return;

      const submitButton = productForm.querySelector('[type="submit"]');
      if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

      const variantInput = form.querySelector('input[name="id"]');
      const quantityInput = form.querySelector('input[name="quantity"]') || root.querySelector('input[name="quantity"]');
      const mainVariantId = variantNumericId(variantInput?.value);
      const addonVariantId = addon ? variantNumericId(addon.dataset.variantId) : 0;
      const runnerVariantId = steelState?.isSale ? variantNumericId(steelState.runnerVariant?.id) : 0;
      const quantity = Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);

      if (!mainVariantId || (addon && !addonVariantId) || (steelState?.isSale && !runnerVariantId)) {
        productForm.handleErrorMessage?.(
          'Please choose a valid skate, steel, and service configuration before adding to cart.'
        );
        return;
      }

      productForm.handleErrorMessage?.();
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      const spinner = productForm.querySelector('.loading__spinner');
      spinner?.classList.remove('hidden');

      const selectedOptions = Array.from(
        root.querySelectorAll(
          'variant-radios input[type="radio"]:checked, variant-selects select option:checked'
        )
      )
        .map((node) => node.value || node.textContent?.trim())
        .filter(Boolean);

      const parentTitle =
        addon?.dataset.parentTitle ||
        steelState?.card?.dataset.parentTitle ||
        document.title;
      const serviceFor = selectedOptions.length
        ? `${parentTitle} — ${selectedOptions.join(' / ')}`
        : parentTitle;

      const mainProperties = steelState ? buildSteelProperties(steelState) : {};
      const items = [
        Object.keys(mainProperties).length
          ? { id: mainVariantId, quantity, properties: mainProperties }
          : { id: mainVariantId, quantity },
      ];

      if (steelState?.isSale) {
        items.push({
          id: runnerVariantId,
          quantity,
          properties: {
            'Runner for': serviceFor,
            'Matched runner size': `${padHolder(steelState.holder)} / ${steelState.mm} mm`,
          },
        });
      }

      if (addon) {
        const serviceProperties = { 'Service for': serviceFor };
        if (serviceConfig.profile) serviceProperties['ProSharp profile'] = serviceConfig.profile;
        if (serviceConfig.sharpening) serviceProperties['Sharpening hollow'] = serviceConfig.sharpening;

        items.push({
          id: addonVariantId,
          quantity,
          properties: serviceProperties,
        });
      }

      const cart = document.querySelector('cart-drawer');
      const payload = { items };

      if (cart) {
        payload.sections = cart.getSectionsToRender().map((section) => section.id);
        payload.sections_url = window.location.pathname;
        cart.setActiveElement?.(document.activeElement);
      }

      try {
        const response = await fetch(routes.cart_add_url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (!response.ok || data.status) {
          const message =
            data.description ||
            data.message ||
            'We could not add the configured skates to cart. Please check your steel and service choices and try again.';
          productForm.handleErrorMessage?.(message);
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'sportszone-product-configurator',
            productVariantId: mainVariantId,
            cartData: data,
          });
        }

        if (cart && data.sections) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(data);
        } else {
          window.location = window.routes?.cart_url || routes.cart_url;
        }
      } catch (error) {
        console.error(error);
        productForm.handleErrorMessage?.(
          'We could not add the configured skates to cart. Please try again.'
        );
      } finally {
        submitButton.classList.remove('loading');
        submitButton.removeAttribute('aria-disabled');
        spinner?.classList.add('hidden');
      }
    },
    true
  );
})();
